import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from "react"

import { createContext, useContextSelector } from "use-context-selector"

import { MyCAppApp } from "../config/mycappapp"
import { FirebaseApp } from "firebase/app"
import { Firestore } from "firebase/firestore"
import { Auth } from "firebase/auth"
import { FirebaseStorage } from "firebase/storage"
import { cycles } from "@ap-frontend/questions"

export const MyCAppAppContext = createContext<MyCAppApp | undefined>(undefined)
MyCAppAppContext.displayName = "MyCAppAppContext"

/** A factory function that asynchronously creates a MyCAppApp. */
export type LoadMyCappApp = (options: {
  signal?: AbortSignal
}) => Promise<MyCAppApp>

export type MyCAppAppProviderProviderProps = PropsWithChildren<{
  loadMyCAppApp?: LoadMyCappApp
  myCAppApp?: MyCAppApp
}>

/**
 * Provides MyCApp's global state as a MyCAppApp object.
 *
 * By the config is loaded using the loadMyCAppApp prop's function, defaulting to
 * `MyCAppApp.fromLoadedDeploymentConfig`.
 *
 * If the `myCAppApp` prop is set to a config value, the config is used directly
 * instead of loading one.
 */
export const MyCAppAppProvider = ({
  children,
  loadMyCAppApp = MyCAppApp.fromLoadedDeploymentConfig,
  myCAppApp,
}: MyCAppAppProviderProviderProps): ReactElement | null => {
  const [app, setApp] = useState<MyCAppApp | undefined>(myCAppApp)
  const [error, setError] = useState<unknown>()

  // Asynchronously load the app once on mount.
  useEffect(() => {
    // Don't load a remote config if one is passed directly
    if (myCAppApp) return

    const abort = new AbortController()

    loadMyCAppApp({ signal: abort.signal })
      .then(app => {
        if (abort.signal.aborted) return

        // TODO: remove this warning once build-time cycle data is not referenced anywhere.
        if (app.deploymentConfig.uiConfig.cycleYear !== cycles.cycleYear) {
          console.warn(`\
Inconsistent cycle year: The run-time deployment configuration's cycleYear \
(${app.deploymentConfig.uiConfig.cycleYear}) differs from the build-time cycleYear \
(${cycles.cycleYear}). Currently not all logic/UI uses the deployment \
configuration's cycleYear — the values must match in production environments \
until they do.`)
        }

        setApp(app)
      })
      .catch(e => {
        if (!abort.signal.aborted) setError(e)
      })

    return () => abort.abort()
  }, [myCAppApp, loadMyCAppApp])

  if (error) {
    throw error // rely on error boundary to handle this
  }

  return (
    <MyCAppAppContext.Provider value={app}>
      {children}
    </MyCAppAppContext.Provider>
  )
}

type Selector<C, T> = (ctx: C) => T

/** Access the per-deployment MyCApp configuration. */
export function useMyCAppApp(): MyCAppApp | undefined
export function useMyCAppApp<T>(selector: Selector<MyCAppApp | undefined, T>): T
export function useMyCAppApp<T>(
  selector: Selector<MyCAppApp | undefined, T> = app => app as T,
): T | (MyCAppApp | undefined) {
  return useContextSelector(MyCAppAppContext, app => {
    return selector(app)
  })
}

export type FirebaseContextType = {
  readonly firebase: FirebaseApp | undefined
  readonly firestore: Firestore | undefined
  readonly auth: Auth | undefined
  readonly storage: FirebaseStorage | undefined
}

export function useFirebase(): FirebaseContextType
export function useFirebase<T>(selector: Selector<FirebaseContextType, T>): T
export function useFirebase<T>(
  selector: Selector<FirebaseContextType, T> = ctx => ctx as T,
): T | FirebaseContextType {
  return useContextSelector(MyCAppAppContext, app => {
    return selector(
      app ?? {
        firebase: undefined,
        firestore: undefined,
        auth: undefined,
        storage: undefined,
      },
    )
  })
}
